import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  Loader2, ArrowLeft, User, Mail, Building, MapPin, Phone, Calendar,
  CreditCard, Cloud, FileText, MessageSquare, Globe, Euro, Package,
  CheckCircle2, Clock, AlertCircle, XCircle, ExternalLink, Copy,
  HardDrive, Users, Zap, Shield, RefreshCw, Pencil, Save, X, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';

export const AdminCustomerDetail = () => {
  const { customerId } = useParams();
  const { token } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingLexoffice, setEditingLexoffice] = useState(false);
  const [lexofficeId, setLexofficeId] = useState('');
  const [savingLexoffice, setSavingLexoffice] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const [confirmDeleteInvoice, setConfirmDeleteInvoice] = useState(null);
  const [cancellingInvoiceId, setCancellingInvoiceId] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        toast.error('Kunde nicht gefunden');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [customerId, token]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopiert!');
  };

  const handleSaveLexofficeId = async () => {
    setSavingLexoffice(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/customers/${customerId}/lexoffice`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ lexoffice_contact_id: lexofficeId })
      });
      if (response.ok) {
        toast.success('Lexoffice-ID gespeichert');
        setEditingLexoffice(false);
        setData(prev => ({
          ...prev,
          customer: { ...prev.customer, lexoffice_contact_id: lexofficeId || null }
        }));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setSavingLexoffice(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId, source) => {
    setDeletingInvoiceId(invoiceId);
    try {
      const response = await fetch(`${backendUrl}/api/admin/invoices/${invoiceId}?source=${source || 'direct'}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Rechnung gelöscht');
        setData(prev => ({
          ...prev,
          invoices: prev.invoices.filter(inv => inv.id !== invoiceId)
        }));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setDeletingInvoiceId(null);
      setConfirmDeleteInvoice(null);
    }
  };

  const handleCancelInvoice = async (invoiceId, source) => {
    setCancellingInvoiceId(invoiceId);
    try {
      const response = await fetch(`${backendUrl}/api/admin/invoices/${invoiceId}/cancel?source=${source || 'direct'}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Rechnung storniert');
        setData(prev => ({
          ...prev,
          invoices: prev.invoices.map(inv =>
            inv.id === invoiceId ? { ...inv, status: 'storniert' } : inv
          )
        }));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Fehler beim Stornieren');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setCancellingInvoiceId(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('de-DE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getOrderStatusBadge = (status) => {
    const configs = {
      'paid': { class: 'bg-green-500/20 text-green-400', label: 'Bezahlt' },
      'pending': { class: 'bg-yellow-500/20 text-yellow-400', label: 'Ausstehend' },
      'failed': { class: 'bg-red-500/20 text-red-400', label: 'Fehlgeschlagen' },
      'cancelled': { class: 'bg-slate-500/20 text-slate-400', label: 'Storniert' },
    };
    return configs[status] || configs['pending'];
  };

  const getInstanceStatusBadge = (status) => {
    const configs = {
      'ready': { class: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'Aktiv' },
      'pending': { class: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Ausstehend' },
      'deploying': { class: 'bg-blue-500/20 text-blue-400', icon: Cloud, label: 'Deploying' },
      'failed': { class: 'bg-red-500/20 text-red-400', icon: AlertCircle, label: 'Fehler' },
    };
    return configs[status] || configs['pending'];
  };

  const getTicketStatusBadge = (status) => {
    const configs = {
      'open': { class: 'bg-blue-500/20 text-blue-400', label: 'Offen' },
      'in_progress': { class: 'bg-yellow-500/20 text-yellow-400', label: 'In Bearbeitung' },
      'waiting_customer': { class: 'bg-purple-500/20 text-purple-400', label: 'Wartet' },
      'resolved': { class: 'bg-green-500/20 text-green-400', label: 'Gelöst' },
      'closed': { class: 'bg-slate-500/20 text-slate-400', label: 'Geschlossen' },
    };
    return configs[status] || configs['open'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Kunde nicht gefunden</p>
        <Button asChild variant="link" className="mt-4 text-blue-400">
          <Link to="/admin/customers">Zurück zur Übersicht</Link>
        </Button>
      </div>
    );
  }

  const { customer, user, orders, subscriptions, invoices, tickets, domains, stats } = data;

  return (
    <div className="space-y-6" data-testid="admin-customer-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="text-slate-400 hover:text-white mb-2">
            <Link to="/admin/customers">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zu Kunden
            </Link>
          </Button>
          <h1 className="text-2xl font-bold text-white">
            {customer.first_name} {customer.last_name}
          </h1>
          {customer.company && (
            <p className="text-slate-400 mt-1">{customer.company}</p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchCustomer} className="border-slate-600 text-slate-300">
          <RefreshCw className="h-4 w-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Gesamtumsatz</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats.total_spent)}</p>
              </div>
              <Euro className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Monatl. Umsatz</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats.monthly_revenue)}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Aktive Abos</p>
                <p className="text-2xl font-bold text-white">{stats.active_subscriptions}</p>
              </div>
              <Cloud className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Support-Tickets</p>
                <p className="text-2xl font-bold text-white">{stats.ticket_count}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="clouds" className="data-[state=active]:bg-slate-700">
            Clouds ({subscriptions.length})
          </TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-slate-700">
            Bestellungen ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-slate-700">
            Rechnungen ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="tickets" className="data-[state=active]:bg-slate-700">
            Tickets ({tickets.length})
          </TabsTrigger>
          <TabsTrigger value="domains" className="data-[state=active]:bg-slate-700">
            Domains ({domains.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Info */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-400" />
                  Kontaktdaten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Kunden-ID</p>
                    <p className="text-white font-mono text-sm flex items-center gap-2">
                      {customer.id.substring(0, 12)}...
                      <button onClick={() => copyToClipboard(customer.id)} className="text-slate-500 hover:text-white">
                        <Copy className="h-3 w-3" />
                      </button>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Kunde seit</p>
                    <p className="text-white">{formatDate(customer.created_at).split(',')[0]}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <a href={`mailto:${customer.email}`} className="hover:text-blue-400">{customer.email}</a>
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Phone className="h-4 w-4 text-slate-500" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {customer.company && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Building className="h-4 w-4 text-slate-500" />
                    <span>{customer.company}</span>
                  </div>
                )}
                
                {/* Lexoffice Contact ID */}
                <div className="pt-3 mt-3 border-t border-slate-700">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-400">Lexoffice Kunden-ID</p>
                    {!editingLexoffice && (
                      <button
                        onClick={() => { setLexofficeId(customer.lexoffice_contact_id || ''); setEditingLexoffice(true); }}
                        className="text-slate-500 hover:text-blue-400"
                        data-testid="edit-lexoffice-btn"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {editingLexoffice ? (
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={lexofficeId}
                        onChange={(e) => setLexofficeId(e.target.value)}
                        placeholder="Lexoffice Contact-ID"
                        className="h-8 bg-slate-900 border-slate-600 text-white text-sm font-mono"
                        data-testid="lexoffice-id-input"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleSaveLexofficeId}
                        disabled={savingLexoffice}
                        className="h-8 px-2 text-green-400 hover:text-green-300"
                      >
                        {savingLexoffice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingLexoffice(false)}
                        className="h-8 px-2 text-slate-400 hover:text-white"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-white font-mono text-sm mt-1 flex items-center gap-2">
                      {customer.lexoffice_contact_id ? (
                        <>
                          {customer.lexoffice_contact_id.substring(0, 20)}{customer.lexoffice_contact_id.length > 20 ? '...' : ''}
                          <button onClick={() => copyToClipboard(customer.lexoffice_contact_id)} className="text-slate-500 hover:text-white">
                            <Copy className="h-3 w-3" />
                          </button>
                        </>
                      ) : (
                        <span className="text-slate-500 italic">Nicht verknüpft</span>
                      )}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-400" />
                  Adresse
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-slate-300">
                  <p>{customer.street || '-'}</p>
                  <p>{customer.zip_code} {customer.city}</p>
                  <p>{customer.country || 'DE'}</p>
                </div>
                {customer.vat_id && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <p className="text-sm text-slate-400">USt-IdNr.</p>
                    <p className="text-white">{customer.vat_id}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* User Account */}
            {user && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-400" />
                    Benutzerkonto
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-400">User-ID</p>
                    <p className="text-white font-mono text-sm">{user.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">E-Mail</p>
                    <p className="text-white">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Auth-Methode</p>
                    <Badge className="bg-slate-700 text-slate-300">{user.auth_method || 'password'}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Registriert</p>
                    <p className="text-white">{formatDate(user.created_at)}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Clouds Tab */}
        <TabsContent value="clouds" className="space-y-4">
          {subscriptions.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="py-12 text-center">
                <Cloud className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500">Keine Clouds vorhanden</p>
              </CardContent>
            </Card>
          ) : (
            subscriptions.map(({ subscription, plan, instance }) => {
              const statusConfig = instance ? getInstanceStatusBadge(instance.status) : { class: 'bg-slate-500/20 text-slate-400', label: 'Keine Instanz' };
              const StatusIcon = statusConfig.icon || Cloud;
              return (
                <Card key={subscription.id} className="bg-slate-800 border-slate-700">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-white font-semibold text-lg">
                            {plan?.custom_domain || 'Cloud'}
                          </h3>
                          <Badge className={statusConfig.class}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        {instance?.url && (
                          <a href={instance.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                            {instance.url}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <HardDrive className="h-4 w-4" />
                            {plan?.storage_gb || 0} GB
                          </span>
                          {plan?.collabora_users > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {plan.collabora_users} Office-Nutzer
                            </span>
                          )}
                          {plan?.hp_backend && (
                            <span className="flex items-center gap-1 text-amber-500">
                              <Zap className="h-4 w-4" />
                              Turbo
                            </span>
                          )}
                          {plan?.antivirus && (
                            <span className="flex items-center gap-1 text-green-500">
                              <Shield className="h-4 w-4" />
                              Antivirus
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-semibold">{formatCurrency(subscription.monthly_price_brutto)}/Monat</p>
                        <p className="text-slate-400 text-sm">Seit {formatDate(subscription.created_at).split(',')[0]}</p>
                        {instance && (
                          <Button asChild variant="outline" size="sm" className="mt-2 border-slate-600 text-slate-300">
                            <Link to={`/admin/clouds/${instance.id}`}>
                              Details
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              {orders.length === 0 ? (
                <p className="text-slate-500 text-center py-12">Keine Bestellungen</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Bestellung</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Datum</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Betrag</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const statusConfig = getOrderStatusBadge(order.status);
                      return (
                        <tr key={order.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-4 px-4">
                            <span className="text-white font-mono text-sm">{order.id.substring(0, 8)}...</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-slate-300">{formatDate(order.created_at).split(',')[0]}</span>
                          </td>
                          <td className="py-4 px-4">
                            <Badge className={statusConfig.class}>{statusConfig.label}</Badge>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="text-white">{formatCurrency(order.total_brutto)}</span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Button asChild variant="ghost" size="sm" className="text-blue-400">
                              <Link to={`/admin/orders/${order.id}`}>Details</Link>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              {invoices.length === 0 ? (
                <p className="text-slate-500 text-center py-12">Keine Rechnungen</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Rechnung</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Quelle</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Datum</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Betrag</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-4 px-4">
                          <span className="text-white">{invoice.invoice_number || (invoice.id ? invoice.id.substring(0, 8) : '-')}</span>
                          {invoice.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{invoice.description}</p>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={invoice.source === 'lexoffice' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-500/20 text-slate-400'}>
                            {invoice.source === 'lexoffice' ? 'Lexoffice' : 'Auto-Billing'}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-300">{formatDate(invoice.created_at).split(',')[0]}</span>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={
                            invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            invoice.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            invoice.status === 'storniert' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }>
                            {invoice.status === 'paid' ? 'Bezahlt' : invoice.status === 'failed' ? 'Fehlgeschlagen' : invoice.status === 'storniert' ? 'Storniert' : invoice.status}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-white">{formatCurrency(invoice.amount_brutto || invoice.amount)}</span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {invoice.status !== 'storniert' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-yellow-400 hover:text-yellow-300 h-7 px-2"
                                disabled={cancellingInvoiceId === invoice.id}
                                onClick={() => handleCancelInvoice(invoice.id, invoice.source)}
                                title="Stornieren"
                              >
                                {cancellingInvoiceId === invoice.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <XCircle className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            {confirmDeleteInvoice === invoice.id ? (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400">Löschen?</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 h-7 px-2"
                                  disabled={deletingInvoiceId === invoice.id}
                                  onClick={() => handleDeleteInvoice(invoice.id, invoice.source)}
                                >
                                  {deletingInvoiceId === invoice.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    'Ja'
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-white h-7 px-2"
                                  onClick={() => setConfirmDeleteInvoice(null)}
                                >
                                  Nein
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => setConfirmDeleteInvoice(invoice.id)}
                                title="Löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              {tickets.length === 0 ? (
                <p className="text-slate-500 text-center py-12">Keine Support-Tickets</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Ticket</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Betreff</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Aktualisiert</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => {
                      const statusConfig = getTicketStatusBadge(ticket.status);
                      return (
                        <tr key={ticket.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-4 px-4">
                            <span className="text-blue-400 font-mono">{ticket.ticket_number}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-white">{ticket.subject}</span>
                          </td>
                          <td className="py-4 px-4">
                            <Badge className={statusConfig.class}>{statusConfig.label}</Badge>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-slate-400 text-sm">{formatDate(ticket.updated_at)}</span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Button asChild variant="ghost" size="sm" className="text-blue-400">
                              <Link to={`/admin/tickets/${ticket.id}`}>Öffnen</Link>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Domains Tab */}
        <TabsContent value="domains">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              {domains.length === 0 ? (
                <p className="text-slate-500 text-center py-12">Keine registrierten Domains</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Domain</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Registriert</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Läuft ab</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((domain) => (
                      <tr key={domain.id || domain.domain} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-4 px-4">
                          <span className="text-white flex items-center gap-2">
                            <Globe className="h-4 w-4 text-slate-500" />
                            {domain.domain}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={domain.status === 'registered' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {domain.status === 'registered' ? 'Registriert' : domain.status}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-300">{formatDate(domain.created_at || domain.registered_at).split(',')[0]}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-300">{domain.expires_at ? formatDate(domain.expires_at).split(',')[0] : '-'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
